import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { BarChartData } from "../../../../types/recharts/barChart";
import { Legend } from "../../../Charts/Legend/Legend";
import { LegendProps } from "../../../Charts/Legend/Legend.type";
import { StackedHorizontalBarChart } from "../../../Charts/StackedHorizontalBarChart/StackedHorizontalBarChart";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";

import { COLLECTION_OF_WEEE_BY_EU_MEMBER_STATES_CHART_DATA_KEYS } from "./CollectionOfWEEEByEUMemberStates.const";
import { CollectionOfWEEEByEUMemberStatesData, CollectionOfWEEEByEUMemberStatesProps } from "./CollectionOfWEEEByEUMemberStates.type";

const CollectionOfWEEEByEUMemberStates: FC<CollectionOfWEEEByEUMemberStatesProps> = ({ data }) => {
	const { t } = useTranslation();
	const chartSize = useBreakpoint();

	const [legendDirection, setLegendDirection] = useState<LegendProps["direction"]>("horizontal");

	const translatedBarChartData = useMemo<BarChartData>(() => {
		return data.map((dataEntry) => ({
			...dataEntry,
			name: t(`pages.pillar.pillar1.collectionOfWEEEByEUMemberStates.blocks.chart.yAxisLabels.${dataEntry.name}`),
		}));
	}, [data, t]);

	const legendData = useMemo(() => {
		return COLLECTION_OF_WEEE_BY_EU_MEMBER_STATES_CHART_DATA_KEYS.map((dataKey) => {
			const label = typeof dataKey?.translation === "string" ? t(dataKey?.translation) : dataKey.name;

			return {
				color: dataKey.color,
				label: label,
			};
		});
	}, [t]);

	useEffect(() => {
		switch (chartSize) {
			case "xs":
			case "sm":
				setLegendDirection("vertical");
				break;

			case "md":
			case "lg":
			case "xl":
			default:
				setLegendDirection("horizontal");
				break;
		}
	}, [chartSize]);

	const endOfBarLabelValueAccessor = useCallback((entry: CollectionOfWEEEByEUMemberStatesData): string => {
		const total =
			entry?.large +
				entry?.small +
				entry?.other || 0;
		return `${new Intl.NumberFormat("nl-BE").format(total)} kg`;
	}, []);

	return (
		<div className="flex flex-col gap-8">

			<div className="self-stretch md:self-center max-w-4xl mt-4">
				<Legend items={legendData} direction={legendDirection} />
			</div>

			<StackedHorizontalBarChart
				YAxisLabel="Collected Waste Electrical and Electronic Equipment (WEEE)"
				data={translatedBarChartData}
				dataKeys={COLLECTION_OF_WEEE_BY_EU_MEMBER_STATES_CHART_DATA_KEYS}
				YAxisProps={{
					dataKey: "name",
					padding: { top: 10, bottom: 10 },
					fontSize: 14,
				}}
				XAxisProps={{ unit: " kg", tickCount: 5, tick: { fontSize: 12 } }}
				showBarLabel={false}
				tooltipUnit=" kg"
				endOfBarLabelValueAccessor={endOfBarLabelValueAccessor}
				chartSubtitle="in kg per inhabitant"
			/>
		</div>
	);
};

export { CollectionOfWEEEByEUMemberStates };